import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Loader2, TrendingUp, MessageSquare, Send, XCircle, IndianRupee, Zap } from "lucide-react";
import { PageHeader } from "@/pages/Accounts";
import { Link } from "react-router-dom";

const RANGES = ["7d", "30d", "90d"];

export default function DashboardPage() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [range, setRange] = useState(sp.get("range") || "30d");
  const [scope, setScope] = useState(sp.get("scope") || ""); // e.g. "account:<id>" or "project:<id>"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (user?.role === "admin") {
          const [a, p] = await Promise.all([
            api.get("/accounts").catch(() => ({ data: [] })),
            api.get("/projects").catch(() => ({ data: [] })),
          ]);
          setAccounts(a.data || []);
          setProjects(p.data || []);
          if (!scope && a.data?.length) setScope(`account:${a.data[0].id}`);
        } else {
          const p = await api.get("/projects");
          setProjects(p.data || []);
          if (!scope && p.data?.length) setScope(`project:${p.data[0].id}`);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  useEffect(() => {
    if (!scope) return;
    setLoading(true);
    const [kind, id] = scope.split(":");
    const path = kind === "account"
      ? `/accounts/${id}/dashboard?range=${range}`
      : `/projects/${id}/dashboard?range=${range}`;
    api.get(path).then(r => setData(r.data)).catch(e => {
      setData(null);
      toast.error(formatApiError(e));
    }).finally(() => setLoading(false));
    setSp({ scope, range }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, range]);

  const totals = data?.totals || {};
  const rate = data?.delivery_rate_percent || 0;
  const daily = data?.daily_series || [];
  const maxSent = Math.max(1, ...daily.map(d => d.sent + d.delivered + d.failed));

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Dashboard"
        description="Delivery health, campaign volume and cost across your scope."
      />

      {/* Quick Send hero CTA */}
      <Card
        className="border-primary/50 bg-gradient-to-br from-primary/10 via-primary/5 to-background overflow-hidden"
        data-testid="dash-quicksend-hero"
      >
        <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Zap className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold tracking-tight">Send SMS in under a minute</h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-lg">
                Upload a CSV, pick a template, send. No project setup. Existing scrubs and delivery tracking all work automatically.
              </p>
            </div>
          </div>
          <Button asChild size="lg" className="shadow-md w-full md:w-auto" data-testid="dash-quicksend-cta">
            <Link to="/quick-send">
              <Zap className="h-4 w-4 mr-2" />Quick Send
            </Link>
          </Button>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="min-w-[240px]">
          <label className="text-xs text-muted-foreground">Scope</label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="dash-scope-select"><SelectValue placeholder="Select scope" /></SelectTrigger>
            <SelectContent>
              {user?.role === "admin" && accounts.map(a => (
                <SelectItem key={`a-${a.id}`} value={`account:${a.id}`}>Account · {a.name}</SelectItem>
              ))}
              {projects.map(p => (
                <SelectItem key={`p-${p.id}`} value={`project:${p.id}`}>Project · {p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Range</label>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[120px]" data-testid="dash-range-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RANGES.map(r => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mb-2" />}
      </div>

      {data && (data.needs_attention?.length > 0 || hasLowConfidence(data)) && (
        <Alert variant="destructive" data-testid="dash-needs-attention">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Needs attention</AlertTitle>
          <AlertDescription className="text-xs">
            One or more campaigns have <b>low parser confidence</b> on delivery reports — vendor response format may have drifted. Review DLR config for the affected campaigns immediately.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid="stat-sent" icon={<Send className="h-4 w-4" />}
          label="Messages sent" value={fmt(totals.messages_sent)} />
        <StatCard testid="stat-delivered" icon={<TrendingUp className="h-4 w-4" />}
          label="Delivered" value={fmt(totals.messages_delivered)}
          sub={`${rate}% rate`} />
        <StatCard testid="stat-failed" icon={<XCircle className="h-4 w-4" />}
          label="Failed" value={fmt(totals.messages_failed)} />
        <StatCard testid="stat-cost" icon={<IndianRupee className="h-4 w-4" />}
          label="Cost" value={`₹${((totals.cost_paise || 0) / 100).toFixed(0)}`}
          sub={`${fmt(totals.campaigns_run)} campaigns`} />
      </div>

      <Card data-testid="dash-daily-card">
        <CardHeader>
          <CardTitle className="text-base">Daily volume</CardTitle>
          <CardDescription>Sent / delivered / failed per day</CardDescription>
        </CardHeader>
        <CardContent>
          {daily.length === 0 ? (
            <div className="text-xs text-muted-foreground py-8 text-center">No activity in this period.</div>
          ) : (
            <div className="flex items-end gap-1 h-40 overflow-x-auto pt-2">
              {daily.map((d, i) => (
                <div key={i} className="flex flex-col items-center gap-1 min-w-[28px]" title={`${d.date}: sent ${d.sent}, delivered ${d.delivered}, failed ${d.failed}`}>
                  <div className="flex flex-col-reverse w-4 h-32 rounded overflow-hidden bg-muted">
                    <div style={{ height: `${(d.delivered / maxSent) * 100}%` }} className="bg-emerald-500 w-full" />
                    <div style={{ height: `${(d.sent / maxSent) * 100}%` }} className="bg-primary w-full" />
                    <div style={{ height: `${(d.failed / maxSent) * 100}%` }} className="bg-destructive w-full" />
                  </div>
                  <div className="text-[9px] text-muted-foreground font-mono rotate-45 origin-left">{d.date?.slice(5)}</div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-3 text-xs mt-4">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-primary" />Sent</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-500" />Delivered</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-destructive" />Failed</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card data-testid="dash-top-camps">
          <CardHeader>
            <CardTitle className="text-base">Top campaigns</CardTitle>
            <CardDescription>By numbers sent</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right">Sent</TableHead>
                  <TableHead className="text-right">Delivery %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.top_campaigns || []).length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-center py-6 text-xs text-muted-foreground">No campaigns.</TableCell></TableRow>
                )}
                {(data?.top_campaigns || []).map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="text-xs">{c.name}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmt(c.sent)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{c.delivery_rate}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card data-testid="dash-failure-reasons">
          <CardHeader>
            <CardTitle className="text-base">Top failure reasons</CardTitle>
            <CardDescription>Across all campaigns in scope</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.top_failure_reasons || []).length === 0 && (
                  <TableRow><TableCell colSpan={2} className="text-center py-6 text-xs text-muted-foreground">No failures 🎉</TableCell></TableRow>
                )}
                {(data?.top_failure_reasons || []).map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs font-mono">{r.reason}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmt(r.count)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-muted-foreground">{icon}</div>
        </div>
        <div className="text-2xl font-semibold mt-1 font-mono tabular-nums">{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function fmt(n) {
  return new Intl.NumberFormat("en-IN").format(n || 0);
}

function hasLowConfidence(data) {
  // Heuristic: parser confidence surfacing is inside individual campaigns;
  // we surface a warning here if there is any explicit `parser_confidence: "low"`
  // in top_campaigns (backend may add this in future). Safe fallback: no-op.
  return (data?.parser_confidence || "") === "low";
}
